import jsPDF from 'jspdf';
import { parseReportMarkdown, runsToText, type InlineRun } from '@/lib/reportMarkdown';

// jsPDF's built-in Helvetica only covers WinAnsi; map common typography to it
// and drop anything else so no glyph renders as garbage.
const WIN_ANSI_EXTRA = new Set('€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ'.split(''));
const REPLACEMENTS: Record<string, string> = {
  '→': '->', '←': '<-', '≥': '>=', '≤': '<=', '≈': '~', '✓': '', '✔': '', '×': 'x', ' ': ' ', ' ': ' ',
};
function pdfSafe(text: string): string {
  let out = '';
  for (const ch of text) {
    if (REPLACEMENTS[ch] !== undefined) out += REPLACEMENTS[ch];
    else if (ch.charCodeAt(0) <= 0xff || WIN_ANSI_EXTRA.has(ch)) out += ch;
  }
  return out;
}

const BRAND: [number, number, number] = [67, 24, 160];
const INK: [number, number, number] = [30, 30, 40];
const MUTED: [number, number, number] = [110, 110, 125];
const RULE: [number, number, number] = [225, 222, 238];
const HEAD_BG: [number, number, number] = [243, 240, 252];

interface PdfOptions {
  title: string;
  generatedAt: string;
  content: string;
  status?: string;
}

export function buildReportPdf({ title, generatedAt, content, status }: PdfOptions): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 56;
  const CW = W - M * 2;
  let y = M;

  const ensure = (h: number) => {
    if (y + h > H - M) { doc.addPage(); y = M; }
  };

  // Draw runs wrapped to a width; returns height used. Bold runs are
  // tokenised per word so mixed bold/regular text wraps correctly.
  const drawRuns = (runs: InlineRun[], x: number, width: number, size: number, opts: { bold?: boolean; color?: [number, number, number]; draw?: boolean } = {}) => {
    const lineH = size * 1.45;
    const words: { w: string; bold: boolean }[] = [];
    for (const r of runs) {
      for (const part of pdfSafe(r.text).split(/(\s+)/)) {
        if (part) words.push({ w: part, bold: !!(r.bold || opts.bold) });
      }
    }
    doc.setFontSize(size);
    let cx = x;
    let lines = 1;
    let cy = y;
    const lineStart = () => { cx = x; };
    for (const { w, bold } of words) {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      const isSpace = /^\s+$/.test(w);
      const ww = doc.getTextWidth(w);
      if (!isSpace && cx + ww > x + width && cx > x) {
        lines++; cy += lineH; lineStart();
      }
      if (isSpace && cx === x) continue;
      if (opts.draw !== false) {
        doc.setTextColor(...(opts.color ?? INK));
        doc.text(w, cx, cy);
      }
      cx += ww;
    }
    return lines * lineH;
  };

  // Header band
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, W, 6, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...INK);
  const titleLines = doc.splitTextToSize(pdfSafe(title), CW) as string[];
  y = M + 6;
  for (const l of titleLines) { doc.text(l, M, y); y += 26; }
  y -= 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...MUTED);
  const meta = [`Generated ${new Date(generatedAt).toLocaleString()}`, status ? `Status: ${status}` : ''].filter(Boolean).join('   |   ');
  doc.text(pdfSafe(meta), M, y);
  y += 12;
  doc.setDrawColor(...RULE);
  doc.line(M, y, W - M, y);
  y += 22;

  for (const b of parseReportMarkdown(content)) {
    switch (b.type) {
      case 'heading': {
        const size = b.level === 1 ? 16 : b.level === 2 ? 13 : 11;
        const h = drawRuns(b.runs, M, CW, size, { bold: true, draw: false });
        ensure(h + 22);
        y += b.level === 1 ? 6 : 10;
        y += size; // baseline
        drawRuns(b.runs, M, CW, size, { bold: true, color: b.level === 2 ? BRAND : INK });
        y += h - size * 1.45 + 4;
        if (b.level === 2) { doc.setDrawColor(...RULE); doc.line(M, y, W - M, y); y += 8; }
        break;
      }
      case 'paragraph': {
        const h = drawRuns(b.runs, M, CW, 10.5, { draw: false });
        ensure(Math.min(h, 60));
        y += 10.5; 
        // split across pages for long paragraphs: draw line-group by simple pagination
        if (y + h - 10.5 > H - M) { doc.addPage(); y = M + 10.5; }
        drawRuns(b.runs, M, CW, 10.5);
        y += h - 10.5 + 6;
        break;
      }
      case 'rule':
        ensure(14); doc.setDrawColor(...RULE); doc.line(M, y + 4, W - M, y + 4); y += 14;
        break;
      case 'list': {
        b.items.forEach((it, idx) => {
          const indent = 16;
          const h = drawRuns(it, M + indent, CW - indent, 10.5, { draw: false });
          ensure(h);
          y += 10.5;
          doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5); doc.setTextColor(...BRAND);
          doc.text(b.ordered ? `${idx + 1}.` : '•', M + 2, y);
          drawRuns(it, M + indent, CW - indent, 10.5);
          y += h - 10.5 + 3;
        });
        y += 5;
        break;
      }
      case 'table': {
        const cols = b.header.length;
        const pad = 6;
        const colW = CW / cols;
        const size = 9.5;
        const rowH = (cells: InlineRun[][], bold: boolean) =>
          Math.max(...cells.map((c) => drawRuns(c, 0, colW - pad * 2, size, { bold, draw: false }))) + pad * 2 - 4;
        const drawRow = (cells: InlineRun[][], bold: boolean, fill?: [number, number, number]) => {
          const h = rowH(cells, bold);
          if (y + h > H - M) { doc.addPage(); y = M; }
          if (fill) { doc.setFillColor(...fill); doc.rect(M, y, CW, h, 'F'); }
          const top = y;
          cells.forEach((c, i) => {
            y = top + pad + size;
            drawRuns(c, M + i * colW + pad, colW - pad * 2, size, { bold });
          });
          y = top + h;
          doc.setDrawColor(...RULE); doc.line(M, y, W - M, y);
        };
        y += 4;
        drawRow(b.header, true, HEAD_BG);
        for (const r of b.rows) drawRow(Array.from({ length: cols }, (_, i) => r[i] ?? [{ text: '' }]), false);
        y += 12;
        break;
      }
    }
  }

  // Footer with page numbers
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...MUTED);
    doc.text(pdfSafe(runsToText([{ text: title }])), M, H - 28);
    doc.text(`Page ${p} of ${pages}`, W - M, H - 28, { align: 'right' });
  }
  return doc;
}

export function downloadReportPdf(opts: PdfOptions, filename: string) {
  buildReportPdf(opts).save(filename);
}
